import { lazy } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { useAuth } from "./auth/contexts/AuthProvider";
import WelcomeVideoManagement from "./dynamicsettings/pages/WelcomeVideo";

// Admin
const Admin = lazy(() => import("./admin/pages/Admin"));
const Dashboard = lazy(() => import("./admin/pages/Dashboard"));
const Faq = lazy(() => import("./admin/pages/Faq"));
const HelpCenter = lazy(() => import("./admin/pages/HelpCenter"));
const Home = lazy(() => import("./admin/pages/Home"));
const Profile = lazy(() => import("./admin/pages/Profile"));
const ProfileActivity = lazy(() => import("./admin/pages/ProfileActivity"));
const ProfileInformation = lazy(
  () => import("./admin/pages/ProfileInformation"),
);
const ProfilePassword = lazy(() => import("./admin/pages/ProfilePassword"));

// Auth
const ForgotPassword = lazy(() => import("./auth/pages/ForgotPassword"));
const ForgotPasswordSubmit = lazy(
  () => import("./auth/pages/ForgotPasswordSubmit"),
);
const Login = lazy(() => import("./auth/pages/Login"));

// Videos
const VideoManagement = lazy(() => import("./videos/pages/VideoManagement"));
const LibraryVideos = lazy(
  () => import("./videos/pages/library/LibraryVideos"),
);
const VideoPool = lazy(() => import("./videos/pages/VideoPool"));
const RemovedVideos = lazy(() => import("./videos/pages/RemovedVideos"));

// Workouts
const PreRidePage = lazy(() => import("./workouts/pages/PreRide"));
const PostRidePage = lazy(() => import("./workouts/pages/PostRide"));
const HomeWorkoutsPage = lazy(() => import("./workouts/pages/HomeWorkouts"));
const MobilityWorkoutsPage = lazy(
  () => import("./workouts/pages/MobilityWorkouts"),
);
const GymWarmupPage = lazy(() => import("./workouts/pages/gym/Warmup"));
const SpeedAndCoordinationPage = lazy(
  () => import("./workouts/pages/gym/SpeedAndCoordination"),
);
const StrengthAndAccessoryPage = lazy(
  () => import("./workouts/pages/gym/GymStrengthAndAccessory"),
);
const CircuitWorkoutPage = lazy(
  () => import("./workouts/pages/gym/CircuitWorkout"),
);
const CardioWorkoutPage = lazy(() => import("./workouts/pages/Cardio"));
const CardioWorkoutV2Page = lazy(() => import("./workouts/pages/CardioV2"));
const CardioCategoryPage = lazy(
  () => import("./workouts/pages/CardioCategory"),
);

// Programs
const ProgramManagement = lazy(
  () => import("./v2content/programs/pages/ProgramManagement"),
);
// Categories
const CategoryManagement = lazy(
  () => import("./v2content/categories/pages/CategoryManagement"),
);
// Daily tips
const DailyTipManagement = lazy(
  () => import("./v2content/dailytips/pages/DailytipsManagement"),
);
// Collections
const CollectionManagement = lazy(
  () => import("./v2content/collections/pages/CollectionManagement"),
);

// Dynamic app settings
const ForcedUpdatesManagement = lazy(
  () => import("./dynamicsettings/pages/ForcedUpdates"),
);
const IntroPagesManagement = lazy(
  () => import("./dynamicsettings/pages/IntroPages"),
);

const V2DynamicSettings = lazy(
  () => import("./v2content/v2dynamicsettings/V2DynamicSettings"),
);

const MaintenanceSettings = lazy(
  () => import("./dynamicsettings/pages/MaintenanceSettings"),
);

// Stripe management
const StripeManagement = lazy(() => import("./stripe/pages/StripeManagement"));

// tools
const MetadataTools = lazy(() => import("./tools/pages/MetadataTools"));
const VideoTransferTool = lazy(() => import("./tools/pages/VideoTransferTool"));
const AITools = lazy(() => import("./tools/pages/AITools"));
// mappings
const MappingsManagement = lazy(
  () => import("./tools/pages/MappingsManagement"),
);

// Topics
const TopicsPage = lazy(() => import("./club/TopicsPage"));
const ClubCategoryPage = lazy(() => import("./club/CategoryPage"));

// Core
const Forbidden = lazy(() => import("./core/pages/Forbidden"));
const NotFound = lazy(() => import("./core/pages/NotFound"));
const UnderConstructions = lazy(
  () => import("./core/pages/UnderConstructions"),
);

// Users
const UserManagement = lazy(() => import("./users/pages/UserManagement"));

export const RouterWrapper = () => {
  const { isLoggedIn } = useAuth();
  return <Routes>{isLoggedIn() ? privateRoutes() : publicRoutes()}</Routes>;
};

const privateRoutes = () => {
  return (
    <>
      <Route path="/" element={<Navigate to={"admin"} replace />} />
      <Route path="admin" element={<Admin />}>
        <Route path="" element={<Home />} />
        <Route path="club">
          <Route path="topics" element={<TopicsPage />} />
          <Route path="categories" element={<ClubCategoryPage />} />
        </Route>
        <Route path="videos">
          <Route path="legacy-videos" element={<VideoManagement />} />
          <Route path="library" element={<LibraryVideos />} />
          <Route path="pool" element={<VideoPool />} />
          <Route path="removed" element={<RemovedVideos />} />
        </Route>
        <Route path="workouts">
          <Route path="pre-ride" element={<PreRidePage />} />
          <Route path="post-ride" element={<PostRidePage />} />
          <Route path="home-workouts" element={<HomeWorkoutsPage />} />
          <Route path="mobility-workouts" element={<MobilityWorkoutsPage />} />
          <Route path="gym">
            <Route path="warmup" element={<GymWarmupPage />} />
            <Route
              path="speed-and-coordination"
              element={<SpeedAndCoordinationPage />}
            />
            <Route
              path="gym-strength-and-accessory"
              element={<StrengthAndAccessoryPage />}
            />
            <Route path="circuit-workout" element={<CircuitWorkoutPage />} />
            <Route path="cardio" element={<CardioWorkoutPage />} />
          </Route>
          <Route path="cardio">
            <Route path="categories" element={<CardioCategoryPage />} />
            <Route path="cardios" element={<CardioWorkoutV2Page />} />
          </Route>
        </Route>
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="faq" element={<Faq />} />
        <Route path="help" element={<HelpCenter />} />
        <Route path="profile" element={<Profile />}>
          <Route path="" element={<ProfileActivity />} />
          <Route path="information" element={<ProfileInformation />} />
          <Route path="password" element={<ProfilePassword />} />
        </Route>
        <Route path="programs" element={<ProgramManagement />} />
        <Route path="collections" element={<CollectionManagement />} />
        <Route path="categories" element={<CategoryManagement />} />
        <Route path="dailytips" element={<DailyTipManagement />} />
        <Route path="user-management" element={<UserManagement />} />
        <Route path="settings">
          <Route path="forced-updates" element={<ForcedUpdatesManagement />} />
          <Route path="intro-pages" element={<IntroPagesManagement />} />
          <Route path="welcome-video" element={<WelcomeVideoManagement />} />
          <Route path="v2settings" element={<V2DynamicSettings />} />
          <Route path="maintenance" element={<MaintenanceSettings />} />
          <Route path="stripe" element={<StripeManagement />} />
        </Route>
        <Route path="tools">
          <Route path="metadata" element={<MetadataTools />} />
          <Route
            path="video-metadata-transfer"
            element={<VideoTransferTool />}
          />
          <Route path="mappings" element={<MappingsManagement />} />
          <Route path="ai" element={<AITools />} />
        </Route>
      </Route>
      <Route path="*" element={<Navigate to={`/`} replace />} />
    </>
  );
};

const publicRoutes = () => {
  return (
    <>
      <Route path="forgot-password" element={<ForgotPassword />} />
      <Route path="forgot-password-submit" element={<ForgotPasswordSubmit />} />
      <Route path="login" element={<Login />} />
      <Route path="under-construction" element={<UnderConstructions />} />
      <Route path="403" element={<Forbidden />} />
      <Route path="404" element={<NotFound />} />
      <Route path="/" element={<Navigate to={"login"} replace />} />
      <Route path="*" element={<Navigate to={`404`} replace />} />
    </>
  );
};

const AppRoutes = () => {
  return <RouterWrapper />;
};

export default AppRoutes;
